import React, { useEffect, useMemo, useState } from 'react'
import { supabase } from '../supabaseClient'

const TRACKS = ['Babes Who Fight Bears', 'Strong & Savage', 'Olympic Weightlifting', 'Private']
const STYPES = ['Warm-Up', 'Strength', 'Accessory', 'Conditioning', 'Core', 'Cooldown', 'Skills', 'Custom']
const SCORE_TYPES = ['No Score', 'Heaviest Set', 'For Time', 'AMRAP', 'Max Reps / Calories', 'Max Distance']
const DAY_OFFSETS = { '1': 0, '2': 2, '3': 4 }
const GRID_DAY_COLUMNS = [0, 4, 8]

const TEMPLATE = `date,track,title,workout_notes,section_type,score_type,section_notes,movement,movement_notes,set_count,reps,load,rpe,demo_url
2026-07-06,Strong & Savage,Back Squat Day,,Strength,Heaviest Set,,Back Squat,,4,3,80%,8,
2026-07-06,Strong & Savage,Back Squat Day,,Accessory,No Score,3 Rounds,Split Squat,,3,10/side,,,
2026-07-08,Strong & Savage,Conditioning,,Conditioning,For Time,,Bike Calories,,1,50,,,`

function clean(value) {
  return String(value || '').trim()
}

function normalize(value) {
  return clean(value).toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_|_$/g, '')
}

function pick(row, names) {
  for (const name of names) {
    const value = row[normalize(name)]
    if (value != null && clean(value) !== '') return clean(value)
  }
  return ''
}

function toISODate(date) {
  if (!date || Number.isNaN(date.getTime())) return ''
  return date.toISOString().split('T')[0]
}

function addDays(date, days) {
  const next = new Date(date)
  next.setDate(next.getDate() + days)
  return next
}

function firstMondayOfMonth(year, monthIndex) {
  const date = new Date(year, monthIndex, 1, 12, 0, 0)
  const offset = (8 - date.getDay()) % 7
  return addDays(date, offset)
}

function inferStartDateFromName(fileName) {
  const months = {
    january: 0, february: 1, march: 2, april: 3, may: 4, june: 5,
    july: 6, august: 7, september: 8, october: 9, november: 10, december: 11
  }
  const match = clean(fileName).toLowerCase().match(/(january|february|march|april|may|june|july|august|september|october|november|december)\s+(\d{4})/)
  if (!match) return ''
  return toISODate(firstMondayOfMonth(parseInt(match[2], 10), months[match[1]]))
}

function parseDelimited(text) {
  const delimiter = text.includes('\t') ? '\t' : ','
  const rows = []
  let row = []
  let cell = ''
  let quoted = false

  for (let i = 0; i < text.length; i++) {
    const char = text[i]
    const next = text[i + 1]

    if (char === '"' && quoted && next === '"') {
      cell += '"'
      i += 1
    } else if (char === '"') {
      quoted = !quoted
    } else if (char === delimiter && !quoted) {
      row.push(cell)
      cell = ''
    } else if ((char === '\n' || char === '\r') && !quoted) {
      if (char === '\r' && next === '\n') i += 1
      row.push(cell)
      if (row.some(v => clean(v))) rows.push(row)
      row = []
      cell = ''
    } else {
      cell += char
    }
  }

  row.push(cell)
  if (row.some(v => clean(v))) rows.push(row)
  return rows
}

function isWeekLabel(value) {
  return /^week\s*\d+\s*:?\s*$/i.test(clean(value))
}

function weekNumber(value) {
  const match = clean(value).match(/week\s*(\d+)/i)
  return match ? parseInt(match[1], 10) : null
}

function isVisualProgrammingGrid(table) {
  return table.some(row => GRID_DAY_COLUMNS.some(col => isWeekLabel(row[col])))
}

function parseRows(text) {
  const table = parseDelimited(text)
  if (table.length < 2) return []

  const headers = table[0].map(normalize)
  return table.slice(1).map(values => {
    const row = {}
    headers.forEach((header, index) => { row[header] = clean(values[index]) })
    return row
  })
}

function validOption(value, options, fallback) {
  const match = options.find(option => option.toLowerCase() === clean(value).toLowerCase())
  return match || fallback
}

function parseSetScheme(value) {
  const raw = clean(value)
  if (!raw) return { count: 0, reps: '', load: '', note: '' }

  const match = raw.match(/^(\d+)\s*x\s*([^\s]+)(?:\s+(.*))?$/i)
  if (match) {
    const extra = clean(match[3])
    const loadMatch = extra.match(/@+\s*(.+)$/)
    return {
      count: parseInt(match[1], 10),
      reps: match[2],
      load: loadMatch ? clean(loadMatch[1]) : '',
      note: loadMatch ? '' : extra
    }
  }

  const countMatch = raw.match(/^(\d+)\s+(.*)$/)
  if (countMatch) {
    return { count: 1, reps: countMatch[1], load: '', note: clean(countMatch[2]) }
  }

  return { count: 1, reps: raw, load: '', note: '' }
}

function looksLikeSetScheme(value) {
  const raw = clean(value)
  return /^\d+\s*x\s*/i.test(raw) || /^\d+\s*-\s*\d+\s*x\s*/i.test(raw)
}

function splitInlineMovementScheme(value) {
  const raw = clean(value)
  const match = raw.match(/^(.+?)\s+(\d+\s*x\s*.+)$/i)
  if (!match) return { name: raw, scheme: '' }
  return { name: clean(match[1]), scheme: clean(match[2]) }
}

function parseAccessoryLine(value) {
  const raw = clean(value)
  if (!raw) return null

  const timeMatch = raw.match(/^(:\d+(?:\s*\/\s*:\d+)?(?:\s*-\s*\d+\s*(?:min|minute|minutes))?)\s+(.+)$/i)
  if (timeMatch) return { name: clean(timeMatch[2]), reps: clean(timeMatch[1]) }

  const metricMatch = raw.match(/^(\d+(?:\/\d+)?(?:\s*-\s*\d+)?\s*(?:ft|feet|m|meter|meters|cal|cals|sec|secs|second|seconds|min|mins|minute|minutes)?)\s+(.+)$/i)
  if (metricMatch) return { name: clean(metricMatch[2]), reps: clean(metricMatch[1]) }

  return { name: raw, reps: '' }
}

function isAccessoryHeading(value) {
  return /^accessory:?$/i.test(clean(value))
}

function isAccessoryNote(value) {
  const raw = clean(value).toLowerCase()
  if (!raw) return false
  return /round|steady|amrap|emom|for time|minute|min|pace/.test(raw) && !/\b(push|pull|squat|row|curl|press|carry|lunge|jump|sit|plank|hold|raise|rdl|bike|run|dead|thruster|swing|extension|fly|v-up|tuck|flip|slam|drag|good morning)\b/.test(raw)
}

function buildSetsFromScheme(schemeText) {
  const scheme = parseSetScheme(schemeText)
  if (!scheme.count) return []
  return Array.from({ length: Math.min(scheme.count, 20) }, (_, index) => ({
    set_number: index + 1,
    reps: scheme.reps,
    load: scheme.load,
    rpe: '',
    order_index: index
  }))
}

function parseGenericSetPlan(row) {
  const setNumber = pick(row, ['set_number', 'set'])
  const setCountRaw = pick(row, ['set_count', 'sets', 'number_of_sets'])
  let reps = pick(row, ['reps', 'rep_scheme'])
  const load = pick(row, ['load', 'weight', 'percent', 'percentage'])
  const rpe = pick(row, ['rpe'])

  if (!setNumber && !setCountRaw && !reps && !load && !rpe) return []

  const scheme = reps || setCountRaw
  const schemeMatch = scheme.match(/^(\d+)\s*x\s*(.+)$/i)
  if (schemeMatch && !setNumber) {
    const count = parseInt(schemeMatch[1], 10)
    reps = reps === scheme ? schemeMatch[2] : reps
    return Array.from({ length: Math.min(count, 20) }, (_, index) => ({
      set_number: index + 1,
      reps,
      load,
      rpe,
      order_index: index
    }))
  }

  if (setNumber) {
    const num = parseInt(setNumber, 10)
    return [{ set_number: Number.isNaN(num) ? 1 : num, reps, load, rpe, order_index: 0 }]
  }

  const count = parseInt(setCountRaw, 10)
  const safeCount = Number.isNaN(count) || count < 1 ? 1 : Math.min(count, 20)
  return Array.from({ length: safeCount }, (_, index) => ({
    set_number: index + 1,
    reps,
    load,
    rpe,
    order_index: index
  }))
}

function dayNumber(dayText) {
  const match = clean(dayText).match(/(\d+)/)
  if (!match) return 1
  return Math.max(1, Math.min(3, parseInt(match[1], 10)))
}

function dateForProgramRow(row, startDate) {
  const base = startDate ? new Date(startDate + 'T12:00:00') : null
  if (!base || Number.isNaN(base.getTime())) return ''
  const week = parseInt(pick(row, ['week']), 10) || 1
  const day = dayNumber(pick(row, ['day']))
  return toISODate(addDays(base, ((week - 1) * 7) + (DAY_OFFSETS[String(day)] || 0)))
}

function dateForGridWorkout(week, day, startDate) {
  const base = startDate ? new Date(startDate + 'T12:00:00') : null
  if (!base || Number.isNaN(base.getTime())) return ''
  return toISODate(addDays(base, ((week - 1) * 7) + (DAY_OFFSETS[String(day)] || 0)))
}

function isStrongSavageFormat(rows) {
  if (!rows.length) return false
  const first = rows[0]
  return Boolean(
    first.week != null &&
    first.day != null &&
    first.warm_up != null &&
    first.main_lift != null &&
    first.sets_reps != null
  )
}

function buildStrongSavageWorkouts(rows, options) {
  const track = options.track || 'Strong & Savage'
  return rows.map(row => {
    const week = pick(row, ['week']) || '1'
    const day = dayNumber(pick(row, ['day']))
    const mainLift = pick(row, ['main_lift']) || 'Main Lift'
    const schemeText = pick(row, ['sets_reps'])
    const scheme = parseSetScheme(schemeText)
    const accessory = pick(row, ['accessory'])
    const circuit = pick(row, ['strongman_circuit', 'conditioning'])
    const coachingNotes = pick(row, ['coaching_notes'])
    const warmUp = pick(row, ['warm_up'])
    const title = `Week ${week} Day ${day} - ${mainLift}`
    const mainNotes = [scheme.note, coachingNotes].filter(Boolean).join(' | ')

    const sections = []

    if (warmUp) {
      sections.push({
        type: 'Warm-Up',
        score_type: 'No Score',
        notes: warmUp,
        movements: []
      })
    }

    sections.push({
      type: 'Strength',
      score_type: 'Heaviest Set',
      notes: mainNotes,
      movements: [{
        name: mainLift,
        notes: scheme.note,
        demo_url: '',
        sets: buildSetsFromScheme(schemeText)
      }]
    })

    if (accessory) {
      sections.push({
        type: 'Accessory',
        score_type: 'No Score',
        notes: accessory,
        movements: [{
          name: accessory,
          notes: '',
          demo_url: '',
          sets: [{ set_number: 1, reps: accessory, load: '', rpe: '', order_index: 0 }]
        }]
      })
    }

    if (circuit) {
      sections.push({
        type: 'Conditioning',
        score_type: circuit.toLowerCase().includes('amrap') ? 'AMRAP' : circuit.toLowerCase().includes('for time') ? 'For Time' : 'No Score',
        notes: circuit,
        movements: []
      })
    }

    return {
      title,
      date: dateForProgramRow(row, options.startDate),
      track,
      notes: coachingNotes,
      assigned_athlete_id: null,
      sections
    }
  })
}

function buildVisualGridWorkouts(table, options) {
  const track = options.track || 'Babes Who Fight Bears'
  const workouts = []

  table.forEach((row, rowIndex) => {
    GRID_DAY_COLUMNS.forEach((startCol, dayIndex) => {
      const week = weekNumber(row[startCol])
      if (!week) return

      const nextWeekIndex = table.findIndex((nextRow, nextIndex) => (
        nextIndex > rowIndex && GRID_DAY_COLUMNS.some(col => weekNumber(nextRow[col]))
      ))
      const endIndex = nextWeekIndex === -1 ? table.length : nextWeekIndex
      const focus = clean(table[rowIndex + 1]?.[startCol]) || `Day ${dayIndex + 1}`
      const tempo = clean(row[startCol + 1])
      const title = `Week ${week} Day ${dayIndex + 1} - ${focus}`
      const strength = {
        type: 'Strength',
        score_type: 'Heaviest Set',
        notes: tempo,
        movements: []
      }
      const accessory = {
        type: 'Accessory',
        score_type: 'No Score',
        notes: '',
        movements: []
      }
      let currentMovement = null

      for (let i = rowIndex + 2; i < endIndex; i++) {
        const strengthCell = clean(table[i]?.[startCol])
        const detailCell = clean(table[i]?.[startCol + 1])
        const accessoryCell = clean(table[i]?.[startCol + 2])

        if (strengthCell && !isAccessoryHeading(strengthCell)) {
          if (looksLikeSetScheme(strengthCell) && currentMovement) {
            const scheme = parseSetScheme(strengthCell)
            currentMovement.notes = [currentMovement.notes, scheme.note, detailCell].filter(Boolean).join(' | ')
            currentMovement.sets = buildSetsFromScheme(strengthCell)
          } else {
            const inline = splitInlineMovementScheme(strengthCell)
            currentMovement = {
              name: inline.name,
              notes: detailCell,
              demo_url: '',
              sets: inline.scheme ? buildSetsFromScheme(inline.scheme) : []
            }
            strength.movements.push(currentMovement)
          }
        } else if (detailCell && currentMovement && !currentMovement.notes.includes(detailCell)) {
          currentMovement.notes = [currentMovement.notes, detailCell].filter(Boolean).join(' | ')
        }

        if (accessoryCell && !isAccessoryHeading(accessoryCell)) {
          if (isAccessoryNote(accessoryCell) && accessory.movements.length === 0) {
            accessory.notes = [accessory.notes, accessoryCell].filter(Boolean).join(' | ')
          } else {
            const parsed = parseAccessoryLine(accessoryCell)
            if (parsed) {
              accessory.movements.push({
                name: parsed.name,
                notes: '',
                demo_url: '',
                sets: [{
                  set_number: 1,
                  reps: parsed.reps,
                  load: '',
                  rpe: '',
                  order_index: 0
                }]
              })
            }
          }
        }
      }

      strength.movements.forEach((movement) => {
        if (!movement.sets.length) {
          movement.sets = [{ set_number: 1, reps: '', load: '', rpe: '', order_index: 0 }]
        }
      })

      const sections = [strength]
      if (accessory.movements.length || accessory.notes) sections.push(accessory)

      workouts.push({
        title,
        date: dateForGridWorkout(week, dayIndex + 1, options.startDate),
        track,
        notes: tempo,
        assigned_athlete_id: null,
        sections
      })
    })
  })

  return workouts
}

function buildGenericWorkouts(rows, members) {
  const memberByEmail = new Map(members.map(member => [clean(member.email).toLowerCase(), member]))
  const memberByName = new Map(members.map(member => [clean(member.name).toLowerCase(), member]))
  const workouts = []
  const workoutMap = new Map()

  rows.forEach((row, rowIndex) => {
    const date = pick(row, ['date', 'workout_date'])
    const title = pick(row, ['title', 'workout', 'workout_title']) || `Workout ${rowIndex + 1}`
    const rawTrack = pick(row, ['track', 'membership_track']) || TRACKS[0]
    const athleteEmail = pick(row, ['athlete_email', 'client_email'])
    const athleteName = pick(row, ['athlete_name', 'client_name'])
    const athlete = athleteEmail
      ? memberByEmail.get(athleteEmail.toLowerCase())
      : athleteName
        ? memberByName.get(athleteName.toLowerCase())
        : null
    const track = athlete ? 'Private' : validOption(rawTrack, TRACKS, TRACKS[0])
    const workoutKey = [date, track, title, athlete?.id || 'public'].join('|')

    if (!workoutMap.has(workoutKey)) {
      const workout = {
        title,
        date,
        track,
        notes: pick(row, ['workout_notes', 'notes']),
        assigned_athlete_id: athlete?.id || null,
        sections: [],
        sectionMap: new Map()
      }
      workoutMap.set(workoutKey, workout)
      workouts.push(workout)
    }

    const workout = workoutMap.get(workoutKey)
    if (!workout.notes) workout.notes = pick(row, ['workout_notes', 'notes'])

    const sectionType = validOption(pick(row, ['section_type', 'section']) || 'Strength', STYPES, 'Strength')
    const scoreType = validOption(pick(row, ['score_type', 'score']) || 'No Score', SCORE_TYPES, 'No Score')
    const sectionNotes = pick(row, ['section_notes'])
    const sectionKey = [sectionType, scoreType, sectionNotes].join('|')

    let section = workout.sectionMap.get(sectionKey)
    if (!section) {
      section = {
        type: sectionType,
        score_type: scoreType,
        notes: sectionNotes,
        movements: [],
        movementMap: new Map()
      }
      workout.sectionMap.set(sectionKey, section)
      workout.sections.push(section)
    }

    const movementName = pick(row, ['movement', 'movement_name', 'exercise'])
    if (!movementName) return

    const movementKey = [movementName, pick(row, ['movement_notes']), pick(row, ['demo_url'])].join('|')
    let movement = section.movementMap.get(movementKey)
    if (!movement) {
      movement = {
        name: movementName,
        notes: pick(row, ['movement_notes']),
        demo_url: pick(row, ['demo_url']),
        sets: []
      }
      section.movementMap.set(movementKey, movement)
      section.movements.push(movement)
    }

    const nextSets = parseGenericSetPlan(row)
    movement.sets.push(...nextSets.map((set, index) => ({
      ...set,
      order_index: movement.sets.length + index
    })))
  })

  return workouts.map(workout => ({
    ...workout,
    sections: workout.sections.map(section => ({
      ...section,
      movements: section.movements.map(movement => ({
        ...movement,
        sets: movement.sets.map((set, index) => ({ ...set, set_number: set.set_number || index + 1, order_index: index }))
      }))
    }))
  }))
}

function buildWorkouts(rows, table, members, options) {
  if (isVisualProgrammingGrid(table)) return buildVisualGridWorkouts(table, options)
  if (isStrongSavageFormat(rows)) return buildStrongSavageWorkouts(rows, options)
  return buildGenericWorkouts(rows, members)
}

export default function SheetImport() {
  const [rawText, setRawText] = useState('')
  const [fileName, setFileName] = useState('')
  const [members, setMembers] = useState([])
  const [track, setTrack] = useState('Strong & Savage')
  const [startDate, setStartDate] = useState('')
  const [posting, setPosting] = useState(false)
  const [toast, setToast] = useState(null)
  const [errors, setErrors] = useState([])

  const showToast = msg => { setToast(msg); setTimeout(() => setToast(null), 3000) }

  useEffect(() => {
    supabase.from('profiles').select('id, name, email').order('name').then(({ data }) => setMembers(data || []))
  }, [])

  const parsedTable = useMemo(() => parseDelimited(rawText), [rawText])
  const parsedRows = useMemo(() => parseRows(rawText), [rawText])
  const detectedStrongSavage = isStrongSavageFormat(parsedRows)
  const detectedVisualGrid = isVisualProgrammingGrid(parsedTable)
  const workouts = useMemo(() => buildWorkouts(parsedRows, parsedTable, members, { startDate, track }), [parsedRows, parsedTable, members, startDate, track])

  const loadFile = async (event) => {
    const file = event.target.files?.[0]
    if (!file) return
    const text = await file.text()
    setFileName(file.name)
    setRawText(text)
    const inferred = inferStartDateFromName(file.name)
    if (inferred) setStartDate(inferred)
  }

  const validate = () => {
    const nextErrors = []
    if (!parsedTable.length) nextErrors.push('No rows found. Upload or paste a CSV/TSV sheet first.')
    if ((detectedStrongSavage || detectedVisualGrid) && !startDate) nextErrors.push('Choose the date for Week 1 Day 1 so the app can place M/W/F workouts correctly.')
    workouts.forEach((workout, index) => {
      if (!workout.date) nextErrors.push(`Workout ${index + 1} is missing a date.`)
      if (!workout.sections.length) nextErrors.push(`${workout.title} has no sections.`)
    })
    setErrors(nextErrors)
    return nextErrors.length === 0
  }

  const postImport = async () => {
    if (!validate()) return
    setPosting(true)

    for (const workout of workouts) {
      const { data: createdWorkout, error: workoutError } = await supabase
        .from('workouts')
        .insert({
          title: workout.title,
          track: workout.track,
          date: workout.date,
          notes: workout.notes,
          assigned_athlete_id: workout.assigned_athlete_id
        })
        .select()
        .single()

      if (workoutError) {
        setPosting(false)
        showToast('Error: ' + workoutError.message)
        return
      }

      for (let sectionIndex = 0; sectionIndex < workout.sections.length; sectionIndex++) {
        const section = workout.sections[sectionIndex]
        const { data: createdSection } = await supabase
          .from('workout_sections')
          .insert({
            workout_id: createdWorkout.id,
            type: section.type,
            score_type: section.score_type,
            notes: section.notes,
            order_index: sectionIndex
          })
          .select()
          .single()

        if (!createdSection) continue

        for (let movementIndex = 0; movementIndex < section.movements.length; movementIndex++) {
          const movement = section.movements[movementIndex]
          const { data: createdMovement } = await supabase
            .from('movements')
            .insert({
              section_id: createdSection.id,
              name: movement.name,
              notes: movement.notes,
              demo_url: movement.demo_url || null,
              scheme: '',
              order_index: movementIndex
            })
            .select()
            .single()

          if (!createdMovement || !movement.sets.length) continue

          await supabase.from('sets').insert(
            movement.sets.map((set, setIndex) => ({
              movement_id: createdMovement.id,
              set_number: set.set_number || setIndex + 1,
              reps: set.reps,
              load: set.load,
              rpe: set.rpe,
              order_index: setIndex
            }))
          )
        }
      }
    }

    setPosting(false)
    showToast(`Imported ${workouts.length} workout${workouts.length === 1 ? '' : 's'}`)
  }

  return (
    <div>
      <div className="section-header">
        <h2 className="section-title">Import Sheet</h2>
      </div>

      <div className="panel">
        <div className="panel-title">Programming Sheet Import</div>
        <p style={{ fontSize: '14px', color: 'var(--charcoal-light)', lineHeight: 1.7, marginBottom: '1rem' }}>
          Upload your monthly programming CSV, paste rows from Google Sheets, or use the detailed import template. For Week/Day sheets, choose the date for Week 1 Day 1 and the app will place Day 1, Day 2, and Day 3 on Monday, Wednesday, and Friday.
        </p>

        <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', marginBottom: '1rem' }}>
          <label className="btn-sm" style={{ display: 'inline-block' }}>
            Choose CSV
            <input type="file" accept=".csv,.tsv,.txt" onChange={loadFile} style={{ display: 'none' }} />
          </label>
          <button className="btn-ghost" onClick={() => { setRawText(TEMPLATE); setFileName('Template') }}>Load Detailed Template</button>
          <button className="btn-ghost" onClick={() => { setRawText(''); setFileName(''); setErrors([]) }}>Clear</button>
        </div>

        {fileName && (
          <div style={{ fontSize: '12px', color: 'var(--moss-light)', marginBottom: '1rem' }}>
            Loaded: {fileName}
          </div>
        )}

        {(detectedStrongSavage || detectedVisualGrid) && (
          <div className="two-col">
            <div className="field">
              <label>Track</label>
              <select value={track} onChange={e => setTrack(e.target.value)}>
                {TRACKS.filter(t => t !== 'Private').map(t => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
            <div className="field">
              <label>Week 1 Day 1 Date</label>
              <input type="date" value={startDate} onChange={e => setStartDate(e.target.value)} />
            </div>
          </div>
        )}

        <div className="field">
          <label>Sheet Data</label>
          <textarea
            value={rawText}
            onChange={e => setRawText(e.target.value)}
            placeholder="Upload a CSV or paste from Google Sheets here..."
            style={{ minHeight: '220px', fontFamily: 'monospace', fontSize: '13px' }}
          />
        </div>
      </div>

      {errors.length > 0 && (
        <div className="panel" style={{ borderColor: 'var(--rose)' }}>
          <div className="panel-title">Fix Before Import</div>
          {errors.map((error, index) => (
            <div key={index} style={{ fontSize: '14px', color: 'var(--rose-light)', marginBottom: '6px' }}>{error}</div>
          ))}
        </div>
      )}

      <div className="panel">
        <div className="panel-title">Preview</div>
        {workouts.length === 0 ? (
          <p className="no-data">No workouts found yet.</p>
        ) : (
          <>
            <div style={{ fontSize: '13px', color: 'var(--charcoal-light)', marginBottom: '1rem' }}>
              {detectedVisualGrid ? 'Visual programming grid detected. ' : detectedStrongSavage ? 'Strong & Savage format detected. ' : ''}
              {workouts.length} workout{workouts.length === 1 ? '' : 's'} ready to import.
            </div>
            {workouts.slice(0, 15).map((workout, index) => (
              <div key={index} className="workout-card">
                <div className="workout-header" style={{ cursor: 'default' }}>
                  <div>
                    <div className="workout-title">{workout.title}</div>
                    <div className="workout-meta">
                      <span className={`track-badge ${workout.track === 'Strong & Savage' ? 'track-strength' : 'track-open'}`}>{workout.track}</span>
                      <span className="future-badge">{workout.date || 'Choose start date'}</span>
                    </div>
                  </div>
                </div>
                <div className="workout-body">
                  {workout.sections.map((section, sectionIndex) => (
                    <div key={sectionIndex} className="section-block">
                      <div className="section-block-title">{section.type} · {section.score_type}</div>
                      {section.notes && <p className="section-block-notes">{section.notes}</p>}
                      {section.movements.length === 0 && <p className="no-data" style={{ padding: 0 }}>No movement rows needed for this section.</p>}
                      {section.movements.map((movement, movementIndex) => (
                        <div key={movementIndex} className="movement-block">
                          <div className="movement-block-name">{movement.name}</div>
                          <div style={{ fontSize: '13px', color: 'var(--charcoal-light)' }}>
                            {movement.sets.length} set{movement.sets.length === 1 ? '' : 's'}
                            {movement.sets[0]?.reps ? ` · ${movement.sets[0].reps}` : ''}
                            {movement.notes ? ` · ${movement.notes}` : ''}
                          </div>
                        </div>
                      ))}
                    </div>
                  ))}
                </div>
              </div>
            ))}
            {workouts.length > 15 && (
              <p style={{ fontSize: '13px', color: 'var(--charcoal-light)', marginBottom: '1rem' }}>
                Showing first 15 workouts.
              </p>
            )}
            <button className="btn-primary" onClick={postImport} disabled={posting}>
              {posting ? 'Importing...' : `Import ${workouts.length} Workout${workouts.length === 1 ? '' : 's'}`}
            </button>
          </>
        )}
      </div>

      {toast && <div className="toast">{toast}</div>}
    </div>
  )
}
